/**
 * V2 admin — Locations tab (V1 admin-locations.js parity).
 *
 * Every location with its yoink count, orphan cleanup (single and
 * bulk), and the geo backfill: locations with coordinates but no
 * state/province get Place-Details-then-reverse-geocode enrichment
 * through the server-side proxy, with an on-page progress log (the
 * user's debugging surface is the page, never a console).
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { getLocationGeo } from '../../../assets/js/location-service.js';
import {
  nearbyPlaces, findPlaceByName, textSearchPlaces, placeDetails, distanceMeters, resolveMapsLink,
} from '../log/places.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { fetchAll } from './data.js';

const SHOW_MAX = 100;
/** Keyset page size for the orphan-id drain, and a runaway backstop. */
const DRAIN_PAGE = 1000;
const DRAIN_MAX_PAGES = 20;

/**
 * Conservative name match for the place-id backfill: the nearby search
 * is already anchored at the location's own coordinates, so a candidate
 * whose normalized name contains (or is contained by) ours is the same
 * place. No match → write NOTHING (a wrong google_place_id would feed
 * wrong details forever). Exported for tests.
 */
export function matchNearbyPlace(displayName, candidates) {
  // Separators stripped entirely, not spaced: "Joe's" and "JOES" must meet
  const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const want = norm(displayName);
  if (!want) return null;
  return candidates.find((c) => {
    const got = norm(c.display_name);
    return got && (got === want || got.includes(want) || want.includes(got));
  }) || null;
}

/**
 * A duplicate google_place_id trips the unique index (Postgres 23505 /
 * PostgREST 409): another row already owns that place. Not an error to
 * shout about — it means this row is a dupe of an already-linked one.
 * Exported for tests.
 */
export function isDuplicateLinkError(error) {
  const m = error?.message || '';
  return m.includes('23505') || m.includes('409') || /duplicate/i.test(m);
}

/**
 * A location is an orphan only when NOTHING references it — no yoinks and no
 * list entries (2026-07-11 report: a Want-to-go place was flagged; the
 * list_entries FK would have refused the delete anyway).
 *
 * `orphanIds` is the authoritative answer from
 * `get_admin_orphan_location_ids` (20260915c), computed inside the database.
 * The browser cannot derive this on its own: list_entries RLS hides other
 * people's private lists, and the admin RPC that worked around that is capped
 * at 1,000 rows, which on 2026-09-15 turned 0 real orphans into a reported
 * 466. A null id-set means "not known" and yields [] — never a guess.
 * Exported for tests.
 */
export function orphanLocations(locations, orphanIds) {
  if (!orphanIds) return [];
  return locations.filter((l) => orphanIds.has(l.id));
}

/**
 * Rows carrying no yoinks that the database does NOT call orphans, so
 * something else references them — a list. Drives the ON A LIST badge.
 * Null id-set means we cannot tell, so we claim nothing.
 */
export function listedLocations(locations, orphanIds) {
  if (!orphanIds) return [];
  return locations.filter((l) => l.yoinkCount === 0 && !orphanIds.has(l.id));
}

/**
 * Pull the readable parts out of a gateway error. rawDelete returns
 * `HTTP <status>: <body>` where the body is PostgREST's JSON — which for the
 * case that actually bites here (a foreign key still pointing at the row)
 * carries code/message/details/hint naming the exact constraint. All of that
 * was being discarded. Exported for tests.
 */
export function parsePostgrestError(error) {
  const raw = error?.message || String(error || 'Unknown error');
  const status = /HTTP (\d{3})/.exec(raw)?.[1] || '';
  let body = null;
  const brace = raw.indexOf('{');
  if (brace !== -1) {
    try { body = JSON.parse(raw.slice(brace)); } catch { body = null; }
  }
  return {
    status,
    raw,
    code: body?.code || '',
    message: body?.message || '',
    details: body?.details || '',
    hint: body?.hint || '',
  };
}

/**
 * Plain-language reading of a failed orphan delete, so the answer is on the
 * page instead of in a console this user does not have. The three shapes that
 * actually occur are worth naming outright — a reviewer should not have to
 * know PostgREST error codes to act on this.
 */
export function explainDeleteFailure(parsed) {
  if (parsed.code === '23503' || /foreign key/i.test(parsed.details || parsed.message)) {
    return 'Something still references these rows, so they are not orphans. '
      + 'The constraint named above says which table.';
  }
  if (parsed.status === '414' || /URI Too Long/i.test(parsed.raw)) {
    return 'The batch made the request URL too long. Smaller batches would fix it.';
  }
  if (parsed.status === '401' || parsed.status === '403' || parsed.code === '42501') {
    return 'The delete was refused for permissions. is_admin() has to be true '
      + 'for the signed-in account, not just for the panel to open.';
  }
  return 'Unrecognised failure — the raw response is above.';
}

/** Search + sort (V1 applyLocationFilter). Exported for tests. */
export function filterLocations(locations, { search = '', sortColumn = 'created_at', sortDirection = 'desc' } = {}) {
  let result = [...locations];
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((l) => (l.display_name || '').toLowerCase().includes(term));
  }
  const numeric = sortColumn === 'yoinks' || sortColumn === 'created_at';
  result.sort((a, b) => {
    const pick = (l) => {
      if (sortColumn === 'yoinks') return l.yoinkCount;
      if (sortColumn === 'created_at') return new Date(l.created_at).getTime();
      return (l.display_name || '').toLowerCase();
    };
    const aVal = pick(a);
    const bVal = pick(b);
    if (numeric) return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  return result;
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let all = [];
  let loaded = false;
  let search = '';
  let sortColumn = 'created_at';
  let sortDirection = 'desc';
  let page = 0; // 0-based page into the filtered list (SHOW_MAX per page)
  let onlyMissingLink = false; // toggle: show only rows without a Google link
  let onlyReported = false; // toggle: show only visitor-reported closures
  let backfill = null; // { running, processed, total, updated, log: [] }
  // Authoritative orphan ids from the database, or null when it could not be
  // asked. Null is a real state the UI renders differently — see render().
  let orphanIds = null;
  // The last delete's outcome, rendered under the toolbar. Persistent on
  // purpose: a toast is gone before it can be read, and this is the only
  // channel there is on a device with no DevTools.
  let lastDeleteReport = null;

  function describeDeleteFailure({ error, attempted, removed, sampleId, rpcError }) {
    const p = parsePostgrestError(error);
    return {
      tone: 'bad',
      title: 'The database refused the delete',
      rows: [
        ['HTTP status', p.status || '(none)'],
        ['Error code', p.code || '(none)'],
        ['Message', p.message || p.raw],
        ['Details', p.details || '(none)'],
        ['Hint', p.hint || '(none)'],
        ['Deleted before it failed', `${removed} of ${attempted}`],
        ['First id in the failing batch', sampleId || '(none)'],
        ['Server-side sweep', rpcError
          ? `unavailable — ${parsePostgrestError(rpcError).message
              || parsePostgrestError(rpcError).raw}`
          : 'not attempted'],
      ],
      plain: explainDeleteFailure(p),
      raw: p.raw,
    };
  }

  function describeSweep(removed, attempted, rpcError) {
    if (removed >= attempted && removed > 0) return null;
    return {
      tone: removed ? 'warn' : 'bad',
      title: removed
        ? 'Some rows were not deleted'
        : 'Nothing was deleted, and nothing errored',
      rows: [
        ['Rows removed', `${removed} of ${attempted}`],
        ['Server-side sweep', rpcError
          ? `unavailable — ${parsePostgrestError(rpcError).message
              || parsePostgrestError(rpcError).raw}`
          : 'used'],
      ],
      plain: removed
        ? 'The rows that stayed are still referenced by something \u2014 a yoink or a '
          + 'list entry \u2014 or were created within the last hour.'
        : 'A delete that matches no rows returns success, not an error. The usual '
          + 'cause is that these are not orphans at all: something still references '
          + 'them and the database simply had nothing to remove. It can also mean '
          + 'is_admin() is false for this account, which makes the rows invisible '
          + 'to the DELETE.',
      raw: '',
    };
  }

  /**
   * When the orphan set is unknown the toolbar must not look like "zero
   * orphans". Say which migration is missing, on the page: this user tests on
   * an iPad and has no console.
   */
  function orphanUnknownHtml() {
    if (orphanIds || !loaded) return '';
    return `
      <details class="admin-diag admin-diag-warn" open>
        <summary>Orphan status could not be determined</summary>
        <p class="admin-muted">
          The count and the bulk delete are hidden rather than guessed. Delete
          is still offered on rows with no yoinks of their own, and the database
          refuses any of those a list still references \u2014 the reason appears
          here when it does.
        </p>
        <dl class="admin-diag-list">
          <dt>Missing</dt><dd>get_admin_orphan_location_ids (supabase/migrations/20260915c_admin_orphan_location_ids.sql)</dd>
          <dt>Why it is not derived here</dt><dd>list_entries RLS hides other people\u2019s private lists, and the admin workaround RPC is capped at 1,000 rows \u2014 on 2026-09-15 that turned 0 real orphans into a reported 466.</dd>
        </dl>
      </details>`;
  }

  function deleteReportHtml() {
    if (!lastDeleteReport) return '';
    const r = lastDeleteReport;
    return `
      <details class="admin-diag admin-diag-${r.tone}" open>
        <summary>${escapeHtml(r.title)}</summary>
        <p class="admin-muted">${escapeHtml(r.plain)}</p>
        <dl class="admin-diag-list">
          ${r.rows.map(([k, v]) => `
            <dt>${escapeHtml(k)}</dt><dd>${escapeHtml(String(v))}</dd>`).join('')}
        </dl>
        ${r.raw ? `<pre class="admin-diag-raw">${escapeHtml(r.raw)}</pre>` : ''}
      </details>`;
  }
  // Manual "Link" picker for the rows auto-match can't resolve (pure
  // addresses with no business name, ambiguous chains): the admin searches
  // Google by the name they can plainly see and picks the real place.
  let linkingId = null;
  let linkQuery = '';
  let linkResults = [];
  let linkStatus = '';

  async function load() {
    const [locations, yoinks, ids] = await Promise.all([
      fetchAll('locations', {
        select: 'id,display_name,lat,lng,google_place_id,status,created_at,county,state_province,country,country_code,reported_closed_at:metadata->>reported_closed_at,status_source:metadata->>status_source',
        order: 'display_name.asc',
      }),
      // id.asc: offset paging over an unordered query is not stable, and a
      // skipped row here invents an orphan that the FK then refuses to delete.
      fetchAll('yoinks', { select: 'location_id', order: 'id.asc' }),
      loadOrphanIds(),
    ]);
    const counts = {};
    yoinks.forEach((y) => {
      if (y.location_id) counts[y.location_id] = (counts[y.location_id] || 0) + 1;
    });
    orphanIds = ids; // Set, or null when the database could not be asked
    all = locations.map((l) => ({ ...l, yoinkCount: counts[l.id] || 0 }));
    loaded = true;
  }

  /**
   * Drain `get_admin_orphan_location_ids` in keyset pages — V2-PLAN §9 bans
   * offset paging, and locations.id is the primary key, so it cursors the
   * pages deterministically (same shape as src/core/drain.js).
   *
   * Returns a Set, or NULL meaning "could not determine". Null is the whole
   * point of this rewrite. The version this replaces returned an EMPTY Set
   * when its RPC was missing or errored, and an empty set reads as "nothing
   * is on any list" — which makes every yoink-less location look deletable.
   * A missing migration, a silent 1,000-row cap and a real answer of zero
   * must not all render identically.
   */
  async function loadOrphanIds() {
    const ids = new Set();
    let cursor = null;
    for (let i = 0; i < DRAIN_MAX_PAGES; i++) {
      const res = await gateway
        .rpc('get_admin_orphan_location_ids', { p_after: cursor, p_limit: DRAIN_PAGE })
        .catch((error) => ({ data: null, error }));
      if (destroyed) return null;
      if (res?.error || !Array.isArray(res?.data)) return null;
      const rows = res.data;
      rows.forEach((row) => { if (row?.location_id) ids.add(row.location_id); });
      if (rows.length < DRAIN_PAGE) return ids;
      cursor = rows[rows.length - 1]?.location_id;
      if (!cursor) return null;
    }
    return null; // still full pages after the backstop: incomplete, so unknown
  }

  const arrow = (col) => (sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '');
  const orphans = () => orphanLocations(all, orphanIds);
  const missingGeo = () => all.filter((l) => l.lat && l.lng && (!l.state_province || !l.country));
  // Pre-place-id-capture rows: without a google_place_id they can never
  // refresh metadata (no closed stamp, no rich details). Coordinates are
  // required — they anchor the confident match.
  const missingPlaceId = () => all.filter((l) => !l.google_place_id && l.lat && l.lng && l.display_name);
  // Visitor "this place is gone" reports awaiting a verdict (the report
  // RPC only flags open rows; any status stamp clears the flag)
  const reported = () => all.filter((l) => l.reported_closed_at);

  function backfillHtml() {
    if (!backfill) return '';
    const pct = backfill.total ? Math.round((backfill.processed / backfill.total) * 100) : 0;
    return `
      <div class="admin-backfill">
        <div class="admin-toolbar">
          <strong>${backfill.running ? `${backfill.label}…` : 'Backfill complete'}</strong>
          <span class="admin-muted">${backfill.processed}/${backfill.total} · ${backfill.updated} updated</span>
          <span class="admin-toolbar-spacer"></span>
          ${!backfill.running ? '<button type="button" class="admin-btn admin-btn-xs" data-clear-backfill>Clear log</button>' : ''}
        </div>
        ${backfill.running ? `<div class="admin-temp-track"><div class="admin-temp-fill" style="width:${pct}%"></div></div>` : ''}
        <div class="admin-backfill-log">
          ${backfill.log.slice(-20).map((line) => `<div>${escapeHtml(line)}</div>`).join('')}
        </div>
      </div>`;
  }

  /** "820 m" / "5.1 mi" — the same-place-or-namesake judgment aid. */
  function fmtDistance(meters) {
    return meters < 400 ? `${Math.round(meters)} m` : `${(meters / 1609.34).toFixed(1)} mi`;
  }

  function linkResultsHtml() {
    const loc = all.find((l) => l.id === linkingId);
    return linkResults.map((r, i) => {
      // Distance from the row's own pin: a similarly-named place miles away
      // is probably a namesake, not this place (the Hollywood Burger trap —
      // "Hollywood Burger & Sandwich Co." sits 5 mi from the WeHo row).
      const meters = loc?.lat != null && loc?.lng != null && r.lat != null && r.lng != null
        ? distanceMeters(loc.lat, loc.lng, r.lat, r.lng) : null;
      const detail = [r.secondaryText, meters != null ? `${fmtDistance(meters)} from this row’s pin` : '']
        .filter(Boolean).join(' · ');
      return `
      <button type="button" class="log-place-option" data-link-pick="${i}">
        <strong>${escapeHtml(r.mainText)}${r.business_status === 'CLOSED_PERMANENTLY'
          ? ' <span class="admin-badge admin-badge-warn">CLOSED</span>' : ''}</strong>
        ${detail ? `<span>${escapeHtml(detail)}</span>` : ''}
      </button>`;
    }).join('');
  }

  function linkRowHtml() {
    const loc = all.find((l) => l.id === linkingId);
    const status = loc?.status;
    return `
      <tr class="admin-link-row"><td colspan="4">
        <div class="admin-link-picker">
          <input type="search" class="admin-search" data-link-search aria-label="Search Google or paste a Maps link" autocomplete="off"
            placeholder="Search Google, or paste a Google Maps share link…" value="${escapeHtml(linkQuery)}">
          <button type="button" class="admin-btn admin-btn-xs" data-link-cancel>Cancel</button>
        </div>
        <div class="admin-muted admin-link-status" data-link-status>${escapeHtml(linkStatus)}</div>
        <div class="admin-link-results" data-link-results>${linkResultsHtml()}</div>
        ${!loc?.google_place_id || loc?.reported_closed_at || loc?.status_source === 'admin' ? `
        <div class="admin-link-mark">
          <span class="admin-muted">${loc?.status_source === 'admin'
            ? 'You ruled on this place by hand, so Google’s re-checks leave it alone. Mark open to hand control back to Google:'
            : loc?.reported_closed_at
              ? 'A visitor reported this place gone. Any stamp below (including Mark open, if the report is wrong) resolves the report — and a closed stamp sticks even while Google still says open:'
              : 'Not on Google anymore? Closed places get delisted — stamp the page yourself:'}</span>
          ${status !== 'closed_permanently' ? `
            <button type="button" class="admin-btn admin-btn-xs" data-mark-status="closed_permanently">Mark permanently closed</button>` : ''}
          ${status !== 'closed_temporarily' ? `
            <button type="button" class="admin-btn admin-btn-xs" data-mark-status="closed_temporarily">Mark temporarily closed</button>` : ''}
          ${(status && status !== 'open') || loc?.reported_closed_at ? `
            <button type="button" class="admin-btn admin-btn-xs" data-mark-status="open">Mark open</button>` : ''}
        </div>` : ''}
      </td></tr>`;
  }

  // Update ONLY the results/status in place — never re-render the table on a
  // keystroke, or the input gets recreated and focus/caret jump (the search
  // felt broken while typing). The picker input stays put; results refresh.
  function updateLinkDom() {
    const results = el.querySelector('[data-link-results]');
    if (results) results.innerHTML = linkResultsHtml();
    const status = el.querySelector('[data-link-status]');
    if (status) status.textContent = linkStatus;
  }

  async function doLinkSearch(term) {
    const loc = all.find((l) => l.id === linkingId);
    const q = (term || '').trim();
    if (q.length < 2) { linkResults = []; linkStatus = ''; return updateLinkDom(); }
    linkStatus = 'Searching…';
    updateLinkDom();
    let results = [];
    let status;
    try {
      if (/^https?:\/\//i.test(q)) {
        // A pasted Google Maps link — the admin can SEE the listing, so go
        // straight to it: expand/parse the link, then search that exact
        // name anchored at the link's own coordinates.
        const parsed = await resolveMapsLink(q);
        if (!parsed?.name) { // placeId-only links have no name to text-search
          status = 'Couldn’t read that link — paste a Google Maps share link.';
        } else {
          results = await textSearchPlaces(parsed.name, parsed.lat ?? loc?.lat, parsed.lng ?? loc?.lng);
          status = results.length ? `Matches for “${parsed.name}” from the link:`
            : `The link points at “${parsed.name}”, but Google’s API no longer returns it — stamp the status below.`;
        }
      } else {
        // Text Search (not autocomplete) so PERMANENTLY-CLOSED places show
        // up; biased at the row's pin so a common name resolves near.
        results = await textSearchPlaces(q, loc?.lat, loc?.lng);
        if (!results.length) {
          // The bare name can miss closed places that the row's own address
          // tail still finds — locality context resurrects delisted-looking
          // listings.
          const tail = (loc?.display_name || '').split(',').slice(1).join(',').trim();
          if (tail) results = await textSearchPlaces(`${q}, ${tail}`, loc?.lat, loc?.lng);
        }
        if (!results.length) {
          // The bias circle itself can suppress a closed branch across town
          // (Burgerlords: Highland Park pin, Chinatown listing) — last try
          // with no bias; the distance line keeps far picks honest.
          results = await textSearchPlaces(q, null, null);
        }
        status = results.length ? ''
          : 'Nothing on Google matches — try another spelling, paste a Google Maps link, or stamp the status below.';
      }
    } catch {
      status = 'Search isn’t reachable right now.';
    }
    // A slower search must not clobber a newer one, or a cancel/close.
    if (destroyed || linkingId == null || linkQuery.trim() !== q) return;
    linkResults = results;
    linkStatus = status;
    updateLinkDom();
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Surveying the territory…</div>';
      return;
    }
    let list = filterLocations(all, { search, sortColumn, sortDirection });
    if (onlyMissingLink) list = list.filter((l) => !l.google_place_id);
    if (onlyReported) list = list.filter((l) => l.reported_closed_at);
    const pageCount = Math.max(1, Math.ceil(list.length / SHOW_MAX));
    if (page > pageCount - 1) page = pageCount - 1;
    const pageStart = page * SHOW_MAX;
    const display = list.slice(pageStart, pageStart + SHOW_MAX);
    const orphanCount = orphans().length;
    const geoCount = missingGeo().length;
    const placeIdCount = missingPlaceId().length;
    const reportedCount = reported().length;
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        ${backfillHtml()}
        <div class="admin-toolbar">
          <input type="search" class="admin-search" data-loc-search aria-label="Search locations" placeholder="Search locations…"
            value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}</span>
          ${orphanIds
            ? (orphanCount ? `<span class="admin-warn-text">${orphanCount} orphans</span>` : '')
            : '<span class="admin-warn-text" title="get_admin_orphan_location_ids is unavailable, so orphan status cannot be determined">orphans unknown</span>'}
          ${geoCount ? `<span class="admin-warn-text">${geoCount} missing geo</span>` : ''}
          ${placeIdCount ? `
            <button type="button" class="admin-warn-text admin-warn-toggle ${onlyMissingLink ? 'is-on' : ''}"
              data-toggle-missing-link
              title="Show only the rows without a Google link">${placeIdCount} missing Google link${onlyMissingLink ? ' ✓' : ''}</button>` : ''}
          ${reportedCount ? `
            <button type="button" class="admin-warn-text admin-warn-toggle ${onlyReported ? 'is-on' : ''}"
              data-toggle-reported
              title="Visitors reported these as gone and Google still says open — verify via Link… or stamp the status">${reportedCount} reported gone${onlyReported ? ' ✓' : ''}</button>` : ''}
          <span class="admin-toolbar-spacer"></span>
          ${geoCount && !backfill?.running ? `
            <button type="button" class="admin-btn" data-backfill-geo>Backfill geo (${geoCount})</button>` : ''}
          ${placeIdCount && !backfill?.running ? `
            <button type="button" class="admin-btn" data-backfill-placeids
              title="Find each location's Google place through the proxy — unblocks the closed-business stamp and rich details on old rows">
              Find Google links (${placeIdCount})</button>` : ''}
          ${orphanIds && orphanCount ? `
            <button type="button" class="admin-btn admin-btn-danger" data-delete-orphans>
              Delete all orphans (${orphanCount})</button>` : ''}
        </div>
        ${orphanUnknownHtml()}
        ${deleteReportHtml()}
        ${list.length ? `
          <div class="admin-pager">
            <span class="admin-muted">
              ${list.length > SHOW_MAX
                ? `Showing ${pageStart + 1}–${pageStart + display.length} of ${list.length}${onlyMissingLink ? ' missing a link' : ''}`
                : `${list.length}${onlyMissingLink ? ' missing a link' : ''}`}
            </span>
            ${pageCount > 1 ? `
              <span class="admin-toolbar-spacer"></span>
              <button type="button" class="admin-btn admin-btn-xs" data-page-prev ${page === 0 ? 'disabled' : ''}>‹ Prev</button>
              <span class="admin-muted">Page ${page + 1} of ${pageCount}</span>
              <button type="button" class="admin-btn admin-btn-xs" data-page-next ${page >= pageCount - 1 ? 'disabled' : ''}>Next ›</button>` : ''}
          </div>` : ''}
        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                ${[['display_name', 'Name'], ['yoinks', 'Yoinks'], ['created_at', 'Created']]
                  .map(([col, label]) => `<th class="admin-sortable" data-sort-locs="${col}">${label}${arrow(col)}</th>`).join('')}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              ${display.length ? display.map((l) => {
                const isOrphan = !!orphanIds && orphanIds.has(l.id);
                const onAList = l.yoinkCount === 0 && !!orphanIds && !orphanIds.has(l.id);
                // With the orphan set unknown, isOrphan is false for every row,
                // which silently removed Delete from the whole table — while the
                // panel above claimed single-row delete still worked. Fall back
                // to the necessary condition the browser can establish on its
                // own (no yoinks), and let the foreign key be the real guard:
                // a refusal now explains itself in the panel (#1418).
                const canDelete = orphanIds ? isOrphan : l.yoinkCount === 0;
                return `
                <tr class="${isOrphan ? 'admin-row-orphan' : ''}">
                  <td>${escapeHtml(l.display_name || '(No name)')}
                    ${onAList ? '<span class="admin-badge" title="Saved to someone’s list — not an orphan">ON A LIST</span>' : ''}
                    ${!l.google_place_id ? '<span class="admin-badge admin-badge-warn" title="No Google link — no closed stamp or rich details on its page">NO GOOGLE LINK</span>' : ''}
                    ${l.status === 'closed_permanently' ? '<span class="admin-badge admin-badge-warn" title="Its page shows the Permanently closed stamp">CLOSED</span>' : ''}
                    ${l.status === 'closed_temporarily' ? '<span class="admin-badge" title="Its page shows the Temporarily closed stamp">TEMP CLOSED</span>' : ''}
                    ${l.reported_closed_at ? `<span class="admin-badge admin-badge-warn" title="A visitor reported this place gone on ${new Date(l.reported_closed_at).toLocaleDateString()} and Google still said open — verify via Link…, or stamp the status (any stamp, including Mark open, resolves the report)">REPORTED GONE</span>` : ''}
                    ${l.status_source === 'admin' ? '<span class="admin-badge" title="You ruled on this place by hand — Google\'s automated re-checks can no longer change it. Mark open to hand control back.">PINNED</span>' : ''}</td>
                  <td class="admin-num">${l.yoinkCount}</td>
                  <td class="admin-cell-dim">${new Date(l.created_at).toLocaleDateString(undefined, { timeZone: 'America/Los_Angeles' })}</td>
                  <td>
                    <div class="admin-actions">
                      <button type="button" class="admin-btn admin-btn-xs" data-view-loc="${l.id}">View</button>
                      <button type="button" class="admin-btn admin-btn-xs" data-link-loc="${l.id}"
                        title="${l.google_place_id ? 'Point this row at a different Google place (fix a wrong auto-link)' : 'Find this row’s Google place'}">
                        ${l.google_place_id ? 'Relink…' : 'Link…'}</button>
                      ${canDelete ? `
                        <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
                          data-delete-loc="${l.id}" data-loc-name="${escapeHtml(l.display_name || '')}"
                          title="${orphanIds
                            ? 'Nothing references this location'
                            : 'No yoinks of its own — the database refuses it if a list still references it'}">Delete</button>` : ''}
                    </div>
                  </td>
                </tr>
                ${linkingId === l.id ? linkRowHtml() : ''}`;
              }).join('') : '<tr><td colspan="4" class="admin-empty-cell">No locations found</td></tr>'}
            </tbody>
          </table>
        </div>
      </div>`;

    el.querySelector('[data-loc-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      page = 0; // a new filter starts at the first page
      render();
      const again = el.querySelector('[data-loc-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));

    const linkInput = el.querySelector('[data-link-search]');
    if (linkInput) {
      // Track the query on every keystroke, but only fire the search once
      // typing pauses — and update results in place, so the field never
      // loses focus mid-word.
      const debouncedSearch = debounce(() => doLinkSearch(linkQuery), 450);
      linkInput.addEventListener('input', (e) => {
        linkQuery = e.target.value;
        debouncedSearch();
      });
    }
  }

  function openLink(locId) {
    const loc = all.find((l) => l.id === locId);
    linkingId = locId;
    // Seed the search with the business name (the part before the address),
    // which is what the admin recognizes and Google matches on.
    linkQuery = (loc?.display_name || '').split(',')[0].trim();
    linkResults = [];
    linkStatus = '';
    render();
    const input = el.querySelector('[data-link-search]');
    if (input) {
      input.focus();
      input.select(); // seeded name is selected — one keystroke overtypes it
      doLinkSearch(linkQuery); // seed a search once (helps business-name rows)
    }
  }

  function cancelLink() {
    linkingId = null;
    linkQuery = '';
    linkResults = [];
    linkStatus = '';
    render();
  }

  /**
   * The picked Google place already belongs to another row: this row IS a
   * duplicate (the backfill's "already linked to another row" skips — the
   * In-N-Out pairs). Offer the real fix: merge this row into the owner via
   * the admin_merge_location RPC (supabase/migrations/20260712f_admin_merge_location.sql), which
   * moves the yoinks and list entries over and deletes the dupe.
   */
  async function offerMerge(owner, dupe) {
    const ok = await confirmDialog({
      title: 'Same place, two rows',
      message: `“${owner.display_name}” already owns that Google place. Merge “${dupe.display_name}” into it? Its ${dupe.yoinkCount} yoink${dupe.yoinkCount === 1 ? '' : 's'} and list entries move over, and this duplicate row is deleted.`,
      confirmLabel: 'Merge them',
    });
    if (!ok || destroyed) return;
    try {
      const { error } = await gateway.rpc('admin_merge_location', {
        p_keeper: owner.id, p_dupe: dupe.id,
      });
      if (destroyed) return;
      if (error) throw error;
      owner.yoinkCount += dupe.yoinkCount;
      owner.listed = owner.listed || dupe.listed;
      all = all.filter((l) => l.id !== dupe.id);
      showToast(`Merged into “${owner.display_name}”.`, { type: 'success' });
      cancelLink();
    } catch {
      if (destroyed) return;
      showToast('Couldn’t merge — has supabase/migrations/20260712f_admin_merge_location.sql been applied?', { type: 'info' });
      linkStatus = '';
      render();
    }
  }

  async function linkPlace(index) {
    const loc = all.find((l) => l.id === linkingId);
    const pick = linkResults[index];
    if (!loc || !pick) return;
    // Duplicate detection BEFORE the write: every row is loaded, so an
    // owner of this google_place_id is findable client-side — no need to
    // bounce off the unique index to learn what we already know.
    const owner = all.find((l) => l.id !== loc.id && l.google_place_id === pick.placeId);
    if (owner) return offerMerge(owner, loc);
    linkStatus = 'Linking…';
    render();
    try {
      // Adopt the place's OWN coordinates/address so the pin stops pointing
      // at the old street geocode (same correction the auto-backfill makes).
      // Text Search already returns them; fall back to Place Details if not.
      let pLat = pick.lat;
      let pLng = pick.lng;
      let address = pick.formatted_address;
      if (pLat == null || pLng == null) {
        const details = await placeDetails(pick.placeId);
        pLat = details.location?.latitude;
        pLng = details.location?.longitude;
        address = address || details.formattedAddress;
      }
      const body = { google_place_id: pick.placeId };
      if (pLat != null && pLng != null) { body.lat = pLat; body.lng = pLng; }
      if (address) body.formatted_address = address;
      const { error } = await gateway.rawUpdate(gateway.TABLES.LOCATIONS, {
        filters: { id: `eq.${loc.id}` }, body,
      });
      if (destroyed) return;
      if (error) {
        showToast(isDuplicateLinkError(error)
          ? 'Another location is already linked to that Google place.'
          : 'Couldn’t link that place — try again.', { type: 'info' });
        linkStatus = '';
        return render();
      }
      Object.assign(loc, body);
      showToast(`Linked “${loc.display_name}”.`, { type: 'success' });
      cancelLink();
    } catch {
      if (destroyed) return;
      linkStatus = 'Couldn’t link that place — try again.';
      render();
    }
  }

  /**
   * Stamp locations.status by hand, for places Google's API has delisted
   * (the Maps app still shows them, the API returns nothing — so no link
   * is possible). The place page already reads location.status as the
   * metadata backstop, so the "Permanently closed" banner appears without
   * any google_place_id. Same narrow RPC the metadata refresh uses.
   */
  async function markStatus(status) {
    const loc = all.find((l) => l.id === linkingId);
    if (!loc) return;
    // admin_set_location_status PINS the verdict so Google's automated
    // writers stop reverting it (supabase/migrations/20260810_admin_status_override.sql).
    // Pre-migration it 404s; the old RPC still stamps, just not durably —
    // which is exactly the behavior this replaces, so the fallback is safe.
    let { error } = await gateway.rawRpc('admin_set_location_status', {
      params: { p_location_id: loc.id, p_status: status },
    });
    if (error) {
      ({ error } = await gateway.rawRpc('set_location_status', {
        params: { p_location_id: loc.id, p_status: status },
      }));
    }
    if (destroyed) return;
    if (error) return showToast('Couldn’t update the status.', { type: 'info' });
    loc.status = status;
    loc.reported_closed_at = null; // any stamp resolves a visitor report (the RPC clears it)
    // Marking closed pins it; marking open releases it back to Google
    loc.status_source = status === 'open' ? null : 'admin';
    showToast(status === 'open'
      ? `“${loc.display_name}” marked open.`
      : `“${loc.display_name}” stamped ${status === 'closed_permanently' ? 'permanently' : 'temporarily'} closed.`,
    { type: 'success' });
    cancelLink();
  }

  async function deleteLocation(locId, name) {
    if (!(await confirmDialog({
      title: `Delete "${name || 'this location'}"?`,
      message: 'This can’t be undone.',
    }))) return;
    const { data, error } = await gateway.rawDelete(gateway.TABLES.LOCATIONS, {
      filters: { id: `eq.${locId}` },
    });
    if (destroyed) return;
    if (error) {
      lastDeleteReport = describeDeleteFailure({
        error, attempted: 1, removed: 0, sampleId: locId,
      });
      showToast('Couldn’t delete it — details below the toolbar.', { type: 'info' });
      return render();
    }
    if (Array.isArray(data) && !data.length) {
      lastDeleteReport = describeSweep(0, 1);
      showToast('Nothing was deleted — details below the toolbar.', { type: 'info' });
      return render();
    }
    lastDeleteReport = null;
    all = all.filter((l) => l.id !== locId);
    showToast('Location deleted.', { type: 'success' });
    render();
  }

  async function deleteAllOrphans() {
    const list = orphans();
    if (!list.length) return;
    if (!(await confirmDialog({
      title: `Delete ${list.length} orphan location${list.length === 1 ? '' : 's'}?`,
      message: 'Orphans have no yoinks and sit on no one’s lists. This can’t be undone.',
    }))) return;
    lastDeleteReport = null;

    // Prefer the server-side sweep (20260727_admin_delete_orphan_locations.sql).
    // It re-decides orphan-ness INSIDE the database, so it cannot act on a
    // stale browser-side list, and it does the whole thing in one statement.
    // 1 = the function's own floor for the age guard.
    const viaRpc = await gateway.rpc('admin_delete_orphan_locations', { p_min_age_hours: 1 })
      .catch((err) => ({ data: null, error: err }));
    if (destroyed) return;
    if (!viaRpc.error && viaRpc.data != null && Number.isFinite(Number(viaRpc.data))) {
      const removed = Number(viaRpc.data);
      lastDeleteReport = describeSweep(removed, list.length);
      showToast(`Deleted ${removed} orphan location${removed === 1 ? '' : 's'}.`,
        { type: removed ? 'success' : 'info' });
      await load();
      if (!destroyed) render();
      return;
    }

    // No RPC (never applied by hand, or refused) — fall back to the REST
    // batches, and this time KEEP the failure. The user tests on an iPad with
    // no DevTools, so an error thrown away here is an error nobody can ever
    // read: swallowing it is what made "Couldn't delete the orphans" a dead
    // end. Every batch reports rows actually removed, because a DELETE that
    // RLS filters to nothing returns 200 with [] rather than an error.
    const ids = list.map((l) => l.id);
    let removed = 0;
    // Batched — one giant in.() filter would blow the URL length
    for (let i = 0; i < ids.length; i += 100) {
      const batch = ids.slice(i, i + 100);
      const { data, error } = await gateway.rawDelete(gateway.TABLES.LOCATIONS, {
        filters: { id: `in.(${batch.join(',')})` },
      });
      if (destroyed) return;
      if (error) {
        lastDeleteReport = describeDeleteFailure({
          error, attempted: list.length, removed, sampleId: batch[0], rpcError: viaRpc.error,
        });
        showToast('Couldn’t delete the orphans — details below the toolbar.', { type: 'info' });
        await load();
        if (!destroyed) render();
        return;
      }
      removed += Array.isArray(data) ? data.length : 0;
    }
    lastDeleteReport = describeSweep(removed, list.length, viaRpc.error);
    showToast(`Deleted ${removed} orphan location${removed === 1 ? '' : 's'}.`,
      { type: removed ? 'success' : 'info' });
    await load();
    if (!destroyed) render();
  }

  async function runBackfill() {
    if (backfill?.running) return;
    const targets = missingGeo();
    if (!targets.length) return showToast('Every location already has geo data.', { type: 'info' });
    backfill = {
      running: true, label: 'Backfilling geo data', processed: 0, total: targets.length, updated: 0,
      log: [`Starting backfill of ${targets.length} locations…`],
    };
    render();
    for (let i = 0; i < targets.length; i++) {
      if (destroyed) return;
      const loc = targets[i];
      try {
        const geo = await getLocationGeo({ placeId: loc.google_place_id, lat: loc.lat, lng: loc.lng });
        const patch = {};
        if (!loc.county && geo.county) patch.county = geo.county;
        if (!loc.state_province && geo.state_province) patch.state_province = geo.state_province;
        if (!loc.country && geo.country) patch.country = geo.country;
        if (!loc.country_code && geo.country_code) patch.country_code = geo.country_code;
        if (Object.keys(patch).length) {
          const { error } = await gateway.rawUpdate(gateway.TABLES.LOCATIONS, {
            filters: { id: `eq.${loc.id}` }, body: patch,
          });
          if (error) {
            backfill.log.push(`ERROR updating ${loc.display_name}: ${error.message || 'unknown'}`);
          } else {
            backfill.updated++;
            Object.assign(loc, patch);
            backfill.log.push(`${loc.display_name}: ${geo.state_province || '—'}, ${geo.country || '—'}`);
          }
        } else {
          backfill.log.push(`No new data for ${loc.display_name}`);
        }
      } catch (err) {
        backfill.log.push(`EXCEPTION for ${loc.display_name}: ${err.message}`);
      }
      backfill.processed++;
      if (i % 5 === 0 || i === targets.length - 1) render();
      await new Promise((r) => setTimeout(r, 200)); // stay under proxy rate limits
    }
    backfill.running = false;
    backfill.log.push(`COMPLETE: updated ${backfill.updated} of ${backfill.total}`);
    if (!destroyed) {
      showToast(`Backfill done — ${backfill.updated} of ${backfill.total} updated.`, { type: 'success' });
      render();
    }
  }

  /**
   * Place-id backfill (2026-07-12, assessment follow-up): rows created
   * before the log flow captured google_place_id can never refresh
   * metadata. For each, a nearby search anchored at the row's own
   * coordinates + a conservative name match; anything ambiguous is
   * skipped and logged — a wrong id is worse than none.
   */
  async function runPlaceIdBackfill() {
    if (backfill?.running) return;
    const targets = missingPlaceId();
    if (!targets.length) return showToast('Every location already has its Google link.', { type: 'info' });
    backfill = {
      running: true, label: 'Finding Google links', processed: 0, total: targets.length, updated: 0,
      log: [`Matching ${targets.length} locations against Google places near their own pins…`],
    };
    render();
    for (let i = 0; i < targets.length; i++) {
      if (destroyed) return;
      const loc = targets[i];
      try {
        const candidates = await nearbyPlaces(loc.lat, loc.lng);
        let match = matchNearbyPlace(loc.display_name, candidates);
        if (!match?.google_place_id) {
          // Nearby Search drops permanently-closed / low-prominence
          // places — the exact rows whose pages show no closed stamp. Fall
          // back to a name search anchored at the pin, which surfaces them.
          match = await findPlaceByName(loc.display_name, loc.lat, loc.lng);
        }
        const owner = match?.google_place_id
          ? all.find((l) => l.id !== loc.id && l.google_place_id === match.google_place_id)
          : null;
        if (owner) {
          // The right place is already linked to another row — this row is a
          // duplicate. Don't bounce off the unique index; say what to do.
          backfill.log.push(
            `DUPLICATE ${loc.display_name}: same place as “${owner.display_name}” — tap Link…, pick that place, and merge them`);
        } else if (match?.google_place_id) {
          // Also correct the pin: these rows were geocoded from an address
          // string, so their lat/lng (and thus Get-Directions) points at the
          // street, not the place. Adopt the matched place's own coordinates.
          const body = { google_place_id: match.google_place_id };
          if (match.lat != null && match.lng != null) { body.lat = match.lat; body.lng = match.lng; }
          if (match.formatted_address) body.formatted_address = match.formatted_address;
          const { error } = await gateway.rawUpdate(gateway.TABLES.LOCATIONS, {
            filters: { id: `eq.${loc.id}` }, body,
          });
          if (error) {
            backfill.log.push(isDuplicateLinkError(error)
              ? `SKIPPED ${loc.display_name}: already linked to another row (duplicate place)`
              : `ERROR updating ${loc.display_name}: ${error.message || 'unknown'}`);
          } else {
            backfill.updated++;
            Object.assign(loc, body);
            backfill.log.push(`${loc.display_name} → ${match.display_name}`);
          }
        } else {
          backfill.log.push(`SKIPPED ${loc.display_name}: no confident match nearby`);
        }
      } catch (err) {
        backfill.log.push(`EXCEPTION for ${loc.display_name}: ${err.message}`);
      }
      backfill.processed++;
      if (i % 5 === 0 || i === targets.length - 1) render();
      await new Promise((r) => setTimeout(r, 200)); // stay under proxy rate limits
    }
    backfill.running = false;
    backfill.log.push(`COMPLETE: linked ${backfill.updated} of ${backfill.total} (skipped rows are listed above — link those by hand via a re-log or leave them)`);
    if (!destroyed) {
      showToast(`Google links done — ${backfill.updated} of ${backfill.total} linked.`, { type: 'success' });
      render();
    }
  }

  function onClick(event) {
    const t = event.target;
    const sortTh = t.closest('[data-sort-locs]');
    if (sortTh) {
      const col = sortTh.dataset.sortLocs;
      if (sortColumn === col) sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      else { sortColumn = col; sortDirection = col === 'created_at' || col === 'yoinks' ? 'desc' : 'asc'; }
      page = 0; cancelLink();
      return;
    }
    if (t.closest('[data-toggle-missing-link]')) {
      onlyMissingLink = !onlyMissingLink;
      page = 0; cancelLink();
      return;
    }
    if (t.closest('[data-toggle-reported]')) {
      onlyReported = !onlyReported;
      page = 0; cancelLink();
      return;
    }
    if (t.closest('[data-page-prev]')) { if (page > 0) { page--; cancelLink(); } return; }
    if (t.closest('[data-page-next]')) { page++; cancelLink(); return; }
    const viewBtn = t.closest('[data-view-loc]');
    if (viewBtn) return router.navigate('location', { id: viewBtn.dataset.viewLoc });
    const linkBtn = t.closest('[data-link-loc]');
    if (linkBtn) return openLink(linkBtn.dataset.linkLoc);
    if (t.closest('[data-link-cancel]')) return cancelLink();
    const markBtn = t.closest('[data-mark-status]');
    if (markBtn) return markStatus(markBtn.dataset.markStatus);
    const pickBtn = t.closest('[data-link-pick]');
    if (pickBtn) return linkPlace(Number(pickBtn.dataset.linkPick));
    const deleteBtn = t.closest('[data-delete-loc]');
    if (deleteBtn) return deleteLocation(deleteBtn.dataset.deleteLoc, deleteBtn.dataset.locName);
    if (t.closest('[data-delete-orphans]')) return deleteAllOrphans();
    if (t.closest('[data-backfill-geo]')) return runBackfill();
    if (t.closest('[data-backfill-placeids]')) return runPlaceIdBackfill();
    if (t.closest('[data-clear-backfill]')) { backfill = null; return render(); }
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load the locations.</div>'; });

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
